/**
 * Plotea paywall (docs/architecture/plotea.md, "Paywall: signed in, no plan"):
 * the active organization has no current Plotea plan, so the Site workspace
 * would only get 403 `product_unavailable`. Instead the user sees this screen:
 * they can still switch to another organization, create one and log out. Until
 * billing exists, access comes from a purchase or a manual pilot grant, so the
 * only call to action is contacting us.
 */
import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Link } from '@tanstack/react-router'
import { Building2, LogOut, Mail, Plus, Users } from 'lucide-react'
import { useAuth } from '../lib/auth'
import { useOrganizations } from '../hooks/useOrganizations'
import { logout } from '../lib/endpoints/auth'
import { qk } from '../lib/queryKeys'
import { TESTID } from '../lib/testids'
import { cn } from '../lib/utils'
import { Button, buttonVariants } from './ui/button'
import { BrandHeader } from './Logo'
import { PloteaLegalLinks } from '../pages/PloteaLegal'
import { CreateOrganizationDialog } from './CreateOrganizationDialog'

export const PLOTEA_CONTACT_EMAIL = 'kontakt@plotea.com'

/** Does the active organization hold a current Plotea plan (`/me` products)? */
export function hasPloteaPlan(user: { products?: readonly string[] }): boolean {
  return (user.products ?? []).includes('plotea')
}

export function ploteaContactHref(organizationName?: string): string {
  const subject = organizationName
    ? `Plan Plotea: ${organizationName}`
    : 'Plan Plotea'
  return `mailto:${PLOTEA_CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}`
}

export function PloteaNoPlanScreen() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const auth = useAuth()
  const { query: orgsQuery, setActive } = useOrganizations()
  const [showCreate, setShowCreate] = useState(false)

  const user = auth.status === 'authenticated' ? auth.user : null
  const activeId = user?.active_org?.id
  const orgs = orgsQuery.data ?? []
  const active = orgs.find((org) => org.id === activeId)
  const others = orgs.filter((org) => org.id !== activeId)
  const canCreate = Boolean(user?.can_create_organization)
  // Owners and admins staff the organization before it buys a plan.
  const role = user?.active_org?.role
  const canManage = role === 'owner' || role === 'admin'

  const enterOrg = (id: string) =>
    setActive.mutate(id, {
      onError: () => toast.error(t('organizations.toast.error')),
    })

  const doLogout = () =>
    void logout().finally(() => {
      qc.removeQueries({ queryKey: qk.me() })
      window.location.assign('/login')
    })

  return (
    <div
      className="flex min-h-screen items-center justify-center bg-background p-4"
      data-testid={TESTID.noPlanScreen}
    >
      <div className="w-full max-w-md rounded-[3px] border border-border bg-card p-6">
        <div className="mb-5">
          <BrandHeader topic="Plan / 01" compact metadata={null} />
        </div>

        <h2 className="text-base font-semibold text-foreground">
          {t('organizations.noPlan.title')}
        </h2>
        {active && (
          <p
            className="mt-1 truncate text-sm font-medium text-foreground"
            data-testid={TESTID.noPlanOrgName}
          >
            {active.name}
          </p>
        )}
        <p className="mt-3 text-sm text-muted-foreground">
          {t('organizations.noPlan.body')}
        </p>
        <p className="mt-2 text-sm text-muted-foreground">
          {t('organizations.noPlan.pilot')}
        </p>

        <a
          href={ploteaContactHref(active?.name)}
          className={cn(buttonVariants(), 'mt-5 w-full gap-2')}
          data-testid={TESTID.noPlanContact}
        >
          <Mail className="size-4" />
          {t('organizations.noPlan.contact', { email: PLOTEA_CONTACT_EMAIL })}
        </a>

        {canManage && (
          <Link
            to="/organization"
            className={cn(
              buttonVariants({ variant: 'outline' }),
              'mt-2 w-full gap-2',
            )}
            data-testid="no-plan-manage-org"
          >
            <Users className="size-4" />
            {t('organizations.noPlan.manage')}
          </Link>
        )}

        {(others.length > 0 || canCreate) && (
          <div className="mt-6">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t('organizations.noPlan.otherOrgs')}
            </h3>
            <div className="space-y-2">
              {others.map((org) => (
                <Button
                  key={org.id}
                  variant="outline"
                  className="w-full justify-start"
                  disabled={setActive.isPending}
                  data-testid={TESTID.noPlanEnterOrg(org.id)}
                  onClick={() => enterOrg(org.id)}
                >
                  <Building2 className="size-4 shrink-0" />
                  <span className="truncate">{org.name}</span>
                </Button>
              ))}
              {canCreate && (
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  data-testid={TESTID.noPlanCreateOrg}
                  onClick={() => setShowCreate(true)}
                >
                  <Plus className="size-4" />
                  {t('organizations.create.button')}
                </Button>
              )}
            </div>
          </div>
        )}

        <div className="mt-6 border-t border-border pt-4">
          <Button
            variant="ghost"
            className="w-full justify-start"
            data-testid={TESTID.noPlanLogout}
            onClick={doLogout}
          >
            <LogOut className="size-4" />
            {t('organizations.onboarding.logout')}
          </Button>
          <PloteaLegalLinks className="mt-3 px-3 text-xs text-muted-foreground" />
        </div>
      </div>
      {canCreate && (
        <CreateOrganizationDialog
          open={showCreate}
          onOpenChange={setShowCreate}
        />
      )}
    </div>
  )
}
